import { useCallback, useState } from 'react';
import notify from '../../../shared/feedback/notifications';
import ISe360User from '../../se360users/ISe360User';
import * as Features from '../../../shared/Features';
import BrokersApi from '../../brokers/BrokersApi';
import * as BrokerTreeTypes from '../../brokers/BrokerTreeTypes';

type UseBrokerNodeEditorsResult = {
    showFirmModal: boolean;
    editingFirmNodeId: number | null;
    showAgentModal: boolean;
    editingAgentNodeId: number | null;
    openBrokerFirmEditor: (brokerFirmName: string) => Promise<void>;
    openBrokerAgentEditor: (brokerAgentName: string) => Promise<void>;
    closeFirmModal: () => void;
    closeAgentModal: () => void;
};

export function useBrokerNodeEditors(user: ISe360User): UseBrokerNodeEditorsResult {
    const [showFirmModal, setShowFirmModal] = useState(false);
    const [editingFirmNodeId, setEditingFirmNodeId] = useState<number | null>(null);
    const [showAgentModal, setShowAgentModal] = useState(false);
    const [editingAgentNodeId, setEditingAgentNodeId] = useState<number | null>(null);

    const openBrokerFirmEditor = useCallback(
        async (brokerFirmName: string) => {
            if (!Features.hasFeature(Features.UpdateBrokerTreeNode, user)) {
                return;
            }

            try {
                const result: any = await BrokersApi.getBrokerTree(undefined, brokerFirmName);
                const nodes = result || [];
                const matched = (function findFirm(nodesList: any[]): any {
                    for (const n of nodesList) {
                        if (
                            (n.type === BrokerTreeTypes.Firm || n.type === 'Firm') &&
                            n.name &&
                            n.name.toString().trim().toLowerCase() === brokerFirmName.toString().trim().toLowerCase()
                        ) {
                            return n;
                        }
                        if (n.children && n.children.length > 0) {
                            const found = findFirm(n.children);
                            if (found) return found;
                        }
                    }
                    return null;
                })(nodes);

                if (matched) {
                    setEditingFirmNodeId(matched.id);
                    setShowFirmModal(true);
                } else {
                    const firstFirm = (function findFirstFirm(nodesList: any[]): any {
                        for (const n of nodesList) {
                            if (n.type === BrokerTreeTypes.Firm || n.type === 'Firm') return n;
                            if (n.children && n.children.length > 0) {
                                const f = findFirstFirm(n.children);
                                if (f) return f;
                            }
                        }
                        return null;
                    })(nodes);

                    if (firstFirm) {
                        setEditingFirmNodeId(firstFirm.id);
                        setShowFirmModal(true);
                    } else {
                        notify.warning('Could not find broker firm to edit');
                    }
                }
            } catch (err) {
                console.error('search broker tree error', err);
                notify.failure('Failed to open broker firm editor');
            }
        },
        [user]
    );

    const openBrokerAgentEditor = useCallback(
        async (brokerAgentName: string) => {
            if (!Features.hasFeature(Features.UpdateBrokerTreeNode, user)) {
                return;
            }

            try {
                const result: any = await BrokersApi.getBrokerTree(undefined, brokerAgentName);
                const nodes = result || [];
                const matched = (function findAgent(nodesList: any[]): any {
                    for (const n of nodesList) {
                        if (
                            (n.type === BrokerTreeTypes.Agent || n.type === 'Agent') &&
                            n.name &&
                            n.name.toString().trim().toLowerCase() === brokerAgentName.toString().trim().toLowerCase()
                        ) {
                            return n;
                        }
                        if (n.children && n.children.length > 0) {
                            const found = findAgent(n.children);
                            if (found) return found;
                        }
                    }
                    return null;
                })(nodes);

                if (matched) {
                    setEditingAgentNodeId(matched.id);
                    setShowAgentModal(true);
                } else {
                    const firstAgent = (function findFirstAgent(nodesList: any[]): any {
                        for (const n of nodesList) {
                            if (n.type === BrokerTreeTypes.Agent || n.type === 'Agent') return n;
                            if (n.children && n.children.length > 0) {
                                const f = findFirstAgent(n.children);
                                if (f) return f;
                            }
                        }
                        return null;
                    })(nodes);

                    if (firstAgent) {
                        setEditingAgentNodeId(firstAgent.id);
                        setShowAgentModal(true);
                    } else {
                        notify.warning('Could not find broker agent to edit');
                    }
                }
            } catch (err) {
                console.error('search broker tree error', err);
                notify.failure('Failed to open broker agent editor');
            }
        },
        [user]
    );

    const closeFirmModal = useCallback(() => {
        setShowFirmModal(false);
        setEditingFirmNodeId(null);
    }, []);

    const closeAgentModal = useCallback(() => {
        setShowAgentModal(false);
        setEditingAgentNodeId(null);
    }, []);

    return {
        showFirmModal,
        editingFirmNodeId,
        showAgentModal,
        editingAgentNodeId,
        openBrokerFirmEditor,
        openBrokerAgentEditor,
        closeFirmModal,
        closeAgentModal
    };
}
